/**
 * @file file-helper.ts
 * @description Утилиты для работы с файлами в Task Manager приложении. Предоставляет набор функций для преобразования файлов в base64, форматирования размеров в читаемые единицы, скачивания и просмотра файлов через FileService API, определения расширений файлов и сохранения Blob объектов на клиентском устройстве для системы управления вложениями.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-01-15
 * @dependencies ['@api/actions/file', 'axios']
 * @related-files ['src/components/ui/file-upload', 'src/types/attachment.ts', 'src/api/actions/file.ts', 'src/forms']
 */

import { FileService } from '@api/actions/file';
import axios, { AxiosError, AxiosResponse } from 'axios';

/**
 * Преобразует файл в строку base64
 * @param file - объект File для преобразования
 * @returns промис со строкой base64 или пустой строкой при ошибке
 */
export const fileToBase64 = async (file: File): Promise<string> => {
    try {
        const result = await toBase64(file);
        return result as string;
    } catch (error) {
        console.error('error on convert file to base64', error);
        return '';
    }
};

/**
 * Форматирует размер файла в байтах в читаемый формат
 * @param bytes - размер в байтах
 * @param decimals - количество десятичных знаков (по умолчанию 2)
 * @returns отформатированная строка с размером (например, '1.23 Mb')
 */
export const formatBytes = (bytes: number, decimals: number = 2) => {
    if (!+bytes) return '0 b';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['b', 'Kb', 'Mb', 'Gb', 'Tb', 'Pb', 'Eb', 'Zb', 'Yb'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

/**
 * Скачивает файл по ссылке и сохраняет с указанным именем
 * @param href - URL для скачивания файла
 * @param filename - имя файла для сохранения
 * TODO: add error handling
 */
export const downloadFile = async (href: string, filename: string) => {
    const response: AxiosResponse | AxiosError = await FileService.get(href);

    //TODO: add error handling
    if (axios.isAxiosError(response)) {
        console.log('api call error', response);
        return;
    }

    downloadBlob(response.data, filename);
};

/**
 * Получает файл по ссылке и открывает его в новом окне браузера
 * @param href - URL файла для просмотра
 * TODO: add error handling
 */
export const showFile = async (href: string) => {
    const response: AxiosResponse | AxiosError = await FileService.get(href);

    //TODO: add error handling
    if (axios.isAxiosError(response)) {
        console.log('api call error', response);
        return;
    }

    const fileUrl = URL.createObjectURL(response.data);
    window.open(fileUrl, '_blank');
};

/**
 * Извлекает расширение файла из имени
 * @param filename - имя файла с расширением
 * @returns расширение файла (без точки) или undefined если расширение отсутствует
 */
export const getFileExtensionByName = (filename: string) => {
    return filename.split('.').pop();
};

/**
 * Скачивает Blob объект как файл на клиентском устройстве
 * @param binaryDataBuffer - данные файла в виде Blob или MediaSource
 * @param filename - имя файла для скачивания
 */
export const downloadBlob = (binaryDataBuffer: Blob | MediaSource, filename: string) => {
    const url = URL.createObjectURL(binaryDataBuffer);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

/**
 * Приватная функция для преобразования файла в base64
 * @param file - объект File
 * @returns промис с результатом FileReader
 */
const toBase64 = (file: File) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
    });
